/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, Button, Table, TableTypes } from "tvs-unified-lib";
import { Fragment, useEffect, useState } from "react";
import { FlexButton } from "../styles";
import { uploadApi } from "@/services";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { useDebounce } from "@/hooks";

interface Props {
  setStep: (step: number) => void;
  fileId: number;
  handleCancel: () => void;
  mandatoryFields: string[];
  setErrorSummaryFileUrl: (url: string) => void;
}

export const Step4 = ({
  fileId,
  setStep,
  handleCancel,
  mandatoryFields,
  setErrorSummaryFileUrl,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [bulkUploadValidate, setBulkUploadValidate] = useState<any>();
  const [tableError, setTableError] = useState<[]>([]);

  const fetchDataError = async () => {
    const params = {
      customer_no: "Customer ID",
      full_name: "Customer Name",
      phone_number: "Customer Mobile No",
      mandatory_fields: mandatoryFields,
    };
    await uploadApi
      .validateMapping(fileId, params)
      .then(({ data: { bulkUpload, validationErrors } }) => {
        if (!bulkUpload) return;
        setBulkUploadValidate(bulkUpload);
        setErrorSummaryFileUrl(bulkUpload?.errorSummaryFileUrl);
        const dataError: any = [
          {
            errorSummary: `${validationErrors?.formatErrorCount} data have format errors`,
          },
          {
            errorSummary: `${validationErrors?.missingInfoCount} data has missing information`,
          },
        ];
        setTableError(dataError);
      })
      .catch((err) => {
        enqueueSnackbar(err, {
          variant: "error",
        });
      });
  };

  useEffect(() => {
    if (!fileId) return;
    fetchDataError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const checkFileStatus = useDebounce(async () => {
    await uploadApi
      .detail(fileId)
      .then(({ data }) => {
        if (data?.status === "success" || data?.status === "failure") {
          setLoading(false);
          navigate("/upload");
        } else {
          checkFileStatus();
        }
      })
      .catch((err) => {
        enqueueSnackbar(err, {
          variant: "error",
        });
      });
  }, 1000);

  const onSubmit = async () => {
    const params = {
      customer_no: "Customer ID",
      full_name: "Customer Name",
      phone_number: "Customer Mobile No",
      mandatory_fields: mandatoryFields,
    };
    setLoading(true)
    await uploadApi
      .updateMapping(fileId, params)
      .then(() => {
        checkFileStatus();
      })
      .catch((err) => {
        enqueueSnackbar(err, {
          variant: "error",
        });
      });
  };

  const columns: ColumnDef<any>[] = [
    {
      header: () => "Error Summary",
      enableSorting: false,
      accessorKey: "errorSummary",
      size: 50,
      cell: ({ getValue }: TableTypes.CellType<any>) => {
        const value = getValue();
        return value;
      },
    },
  ];

  const checkSubmit = bulkUploadValidate?.successCount > 0 && bulkUploadValidate?.failureCount === 0;
  
  return (
    <Fragment>
      {bulkUploadValidate?.successCount > 0 && (
        <Alert
          variant="success"
          withClose={false}
          children={`${bulkUploadValidate?.successCount} data are eligible to upload`}
        />
      )}
      {bulkUploadValidate?.failureCount > 0 && (
        <Alert
          variant="error"
          withClose={false}
          children={`${bulkUploadValidate?.failureCount} data are invalid, and cannot be uploaded.`}
        />
      )}

      <Table
        columns={columns}
        data={tableError}
        isLoading={false}
        pagination={{
          pageIndex: 0,
          pageSize: 2,
        }}
        setSorting={() => {}}
        sorting={[]}
        totalRecords={2}
        enablePagination={false}
        manualSorting={false}
      />
      <FlexButton>
        <Button disabled={isLoading} label="Cancel" variant="outline" onClick={handleCancel} />
        <FlexButton>
          <Button disabled={isLoading} label="Previous" onClick={() => setStep(3)} />
          <Button
            label="Submit"
            disabled={!checkSubmit}
            loading={isLoading}
            onClick={() => {
              onSubmit();
            }}
          />
        </FlexButton>
      </FlexButton>
    </Fragment>
  );
};

export default Step4;
